import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  CircleX, 
  QrCode, 
  CheckCircle2, 
  XCircle, 
  User,
  Ticket,
  Clock,
  AlertCircle,
  SwitchCamera
} from "lucide-react";
import clsx from "clsx";
import { Html5Qrcode } from "html5-qrcode";
import "./LectorQR.css";
import qrAdminService from "../../services/qrAdminService";
import InlineSpinner from "../ui/InlineSpinner";

const QR_FORMATS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const CAMERA_RELEASE_DELAY_MS = 400;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Solo enumerateDevices — nunca abre un stream (seguro con escáner activo). */
const listCamerasFromDevices = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((device) => device.kind === "videoinput")
    .map((device) => ({ id: device.deviceId, label: device.label }));
};

const isMobileOrTablet = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const mobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const tabletUa = /iPad|Tablet|PlayBook/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));
  const touchMac = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return mobileUa || tabletUa || touchMac;
};

const isBackCameraLabel = (label) => {
  if (!label) return false;
  const l = label.toLowerCase();
  return /back|rear|environment|trasera|posterior|trás|wide/i.test(l)
    && !/front|user|frontal|selfie|face/i.test(l);
};

const isFrontCameraLabel = (label) => {
  if (!label) return false;
  const l = label.toLowerCase();
  return /front|user|frontal|selfie|face|facetime/i.test(l);
};

const sortCamerasForMobile = (cameras) => {
  return [...cameras].sort((a, b) => {
    const aBack = isBackCameraLabel(a.label);
    const bBack = isBackCameraLabel(b.label);
    const aFront = isFrontCameraLabel(a.label);
    const bFront = isFrontCameraLabel(b.label);
    if (aBack && !bBack) return -1;
    if (!aBack && bBack) return 1;
    if (aFront && !bFront) return 1;
    if (!aFront && bFront) return -1;
    return 0;
  });
};

const pickBackCameraIndex = (cameras) => {
  if (!cameras?.length) return 0;
  const backIndex = cameras.findIndex((c) => isBackCameraLabel(c.label));
  if (backIndex >= 0) return backIndex;
  if (cameras.length > 1) return cameras.length - 1;
  return 0;
};

/** Opciones de cámara para html5-qrcode (1er argumento de start, sin videoConstraints en config). */
const buildCameraConfig = ({ deviceId, facingMode } = {}) => {
  if (deviceId) return deviceId;
  if (facingMode) return { facingMode };
  return { facingMode: "user" };
};

/**
 * Pide permiso solo si hace falta (sin escáner activo).
 * No usar mientras html5-qrcode está escaneando.
 */
const enumerateVideoCamerasWithPermission = async () => {
  let cameras = await listCamerasFromDevices();
  const needsPermission = cameras.length > 0 && cameras.every((camera) => !camera.label);

  if (!needsPermission) return cameras;

  let stream = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: "user" } },
    });
    cameras = await listCamerasFromDevices();
  } finally {
    stream?.getTracks().forEach((track) => {
      if (track.readyState !== "ended") track.stop();
    });
    await wait(CAMERA_RELEASE_DELAY_MS);
  }

  return cameras;
};

const buildScannerConfig = () => ({
  fps: 15,
  aspectRatio: 1,
  disableFlip: false,
  videoConstraints: {
    width: { min: 480, ideal: 720, max: 1280 },
    height: { min: 480, ideal: 720, max: 1280 }
  },
  formatsToSupport: QR_FORMATS,
  experimentalFeatures: {
    useBarCodeDetectorIfSupported: true,
  },
});

const getCameraLabel = (camera) => {
  if (!camera) return "Cámara";
  if (isBackCameraLabel(camera.label)) return "Trasera";
  if (isFrontCameraLabel(camera.label)) return "Frontal";
  return camera.label || "Cámara";
};

export default function LectorQR({ onClose, evento }) {
  const [loading, setLoading] = useState(true);
  const [invitados, setInvitados] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [boletosConfirmar, setBoletosConfirmar] = useState(1);
  const [procesandoEscaneo, setProcesandoEscaneo] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [switchingCamera, setSwitchingCamera] = useState(false);
  const [startingScanner, setStartingScanner] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerIdRef = useRef("qr-reader");
  const scannerSessionRef = useRef(0);
  const startingScannerRef = useRef(false);
  const availableCamerasRef = useRef([]);
  const currentCameraIndexRef = useRef(0);
  const lastScanTextRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const invitadosRef = useRef([]);
  const pendingConfirmationRef = useRef(null);
  const procesandoEscaneoRef = useRef(false);
  const eventoRef = useRef(evento);

  invitadosRef.current = invitados;
  pendingConfirmationRef.current = pendingConfirmation;
  procesandoEscaneoRef.current = procesandoEscaneo;
  eventoRef.current = evento;

  // Cargar lista de invitados al abrir
  const cargarInvitados = useCallback(async () => {
    if (!evento?.id) return;
    setLoading(true);
    const res = await qrAdminService.obtenerInvitadosQR(evento.id);
    if (res.success) {
      setInvitados(res.data.invitados || []);
    }
    setLoading(false);
  }, [evento]);

  useEffect(() => {
    cargarInvitados();
  }, [cargarInvitados]);

  // Función para procesar el código QR escaneado (useCallback para estabilidad)
  const onScanSuccess = useCallback(async (decodedText) => {
    const invitados = invitadosRef.current;
    const pendingConfirmation = pendingConfirmationRef.current;
    const procesandoEscaneo = procesandoEscaneoRef.current;
    const evento = eventoRef.current;

    // Evitar escaneos duplicados o si hay una confirmación pendiente / procesando
    if (lastScanTextRef.current === decodedText || pendingConfirmation || procesandoEscaneo) {
      return;
    }

    console.log('✅ QR Code detectado:', decodedText);
    lastScanTextRef.current = decodedText;

    // Limpiar timeout anterior si existe
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    // Permitir escanear el mismo código después de 2 segundos
    scanTimeoutRef.current = setTimeout(() => {
      lastScanTextRef.current = null;
    }, 2000);

    try {
      // Formato real del QR: "Nombre | Evento | N boletos | Mesas: X,Y | correo@mail.com"
      // Intentar primero el formato de texto plano (pipe-separated)
      let correoEscaneado = null;
      let invitadoData = null;
      let mesasQR = null;

      // Detectar si contiene pipes (formato actual de los boletos)
      if (decodedText.includes(' | ')) {
        const partes = decodedText.split(' | ').map(p => p.trim());
        // El correo siempre es el último elemento
        const posibleCorreo = partes[partes.length - 1];
        if (posibleCorreo.includes('@')) {
          correoEscaneado = posibleCorreo.toLowerCase();
          console.log('📧 Correo extraído del QR:', correoEscaneado);
        }

        // Buscar parte de mesas en las partes intermedias
        partes.forEach((parte) => {
          const lower = parte.toLowerCase();
          if (lower.includes('mesa')) {
            mesasQR = parte.replace(/^(mesas?:?\s*)/i, '').trim();
          }
        });
      } else {
        // Intentar parsear como JSON (formato compacto)
        try {
          const qrData = JSON.parse(decodedText);
          const invitadoIdEscaneado = qrData.i || qrData.invitado_id;
          const eventoIdEscaneado = qrData.e || qrData.evento_id;

          if (eventoIdEscaneado && evento?.id && eventoIdEscaneado !== evento.id) {
            throw new Error('Este boleto pertenece a otro evento.');
          }

          if (invitadoIdEscaneado) {
            invitadoData = invitados.find(inv =>
              inv.invitado_id === invitadoIdEscaneado ||
              inv.id === invitadoIdEscaneado
            );
          }
        } catch (jsonErr) {
          // Si el texto contiene @, intentar usarlo como correo directamente
          if (decodedText.includes('@')) {
            correoEscaneado = decodedText.trim().toLowerCase();
          }
        }
      }

      // Buscar por correo si no se encontró por ID
      if (!invitadoData && correoEscaneado) {
        invitadoData = invitados.find(inv =>
          inv.correo && inv.correo.toLowerCase() === correoEscaneado
        );
      }

      if (!invitadoData) {
        const identificador = correoEscaneado || decodedText.substring(0, 60);
        throw new Error(`No se encontró un invitado confirmado. Verificar: ${identificador}`);
      }

      console.log('✅ Información del invitado encontrada:', invitadoData);

      // Calcular boletos restantes (total - ya usados desde BD)
      const boletosYaUsados = invitadoData.boletos_escaneados || 0;
      const boletosRestantes = invitadoData.cantidad_boletos - boletosYaUsados;

      if (boletosRestantes <= 0) {
        const scanData = {
          id: `QR-${Date.now()}`,
          timestamp: new Date().toISOString(),
          invitadoId: invitadoData.invitado_id,
          invitado: invitadoData.invitado_nombre,
          mesa: mesasQR || (invitadoData.mesas && Array.isArray(invitadoData.mesas)
            ? invitadoData.mesas.map(m => m.mesa_numero).join(', ')
            : null),
          boletos: 0,
          boletosTotal: invitadoData.cantidad_boletos,
          boletosUsados: boletosYaUsados,
          restricciones: invitadoData.restricciones || { vegetarianos: 0, alergicosMariscos: 0, celiaco: 0, alergicosLactosa: 0 },
          valido: false,
          evento: evento?.nombre_evento || 'Evento',
          error: 'Todos los boletos ya fueron utilizados'
        };
        setLastScan(scanData);
        setScanHistory(prev => [scanData, ...prev].slice(0, 10));
        return;
      }

      // Obtener mesas asignadas
      const mesasDb = invitadoData.mesas && Array.isArray(invitadoData.mesas)
        ? invitadoData.mesas.map(m => m.mesa_numero).join(', ')
        : '';
      const mesasFinal = mesasQR || mesasDb || 'Sin asignar';

      // Mostrar modal de confirmación
      setPendingConfirmation({
        invitadoData: invitadoData,
        boletosRestantes: boletosRestantes,
        boletosYaUsados: boletosYaUsados,
        mesas: mesasFinal
      });
      setBoletosConfirmar(Math.min(boletosRestantes, 1));

    } catch (error) {
      console.error('❌ Error al procesar código QR:', error);
      const errorScanData = {
        id: `QR-${Date.now()}`,
        timestamp: new Date().toISOString(),
        invitadoId: decodedText,
        invitado: 'Error al validar invitación',
        mesa: null,
        boletos: 0,
        restricciones: { vegetarianos: 0, alergicosMariscos: 0, celiaco: 0, alergicosLactosa: 0 },
        valido: false,
        evento: evento?.nombre_evento || 'Evento',
        error: error.message || 'El código QR no es válido para este evento'
      };
      setLastScan(errorScanData);
      setScanHistory(prev => [errorScanData, ...prev].slice(0, 10));
    }
  }, []);

  const onScanError = useCallback(() => {
    // Silenciar errores de escaneo continuo (son normales)
  }, []);

  const isSessionActive = useCallback((session) => (
    isMountedRef.current && session === scannerSessionRef.current
  ), []);

  // Pausa y libera tracks si stop() no alcanzó a ejecutarse (evita play() interrumpido).
  const resetScannerContainer = useCallback(() => {
    const root = document.getElementById(scannerIdRef.current);
    if (root) {
      root.innerHTML = "";
    }
  }, []);

  const releaseMediaTracks = useCallback(() => {
    const root = document.getElementById(scannerIdRef.current);
    if (!root) return;
    root.querySelectorAll("video").forEach((video) => {
      video.onabort = null;
      video.onerror = null;
      const playPromise = video.play?.();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
      try {
        video.pause();
      } catch {
        // ignore
      }
      const stream = video.srcObject;
      if (stream?.getTracks) {
        stream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") track.stop();
        });
      }
      video.srcObject = null;
    });
  }, []);

  const releaseCameraHardware = useCallback(async () => {
    const scanner = html5QrCodeRef.current;
    let stoppedCleanly = false;

    if (scanner) {
      try {
        const state = scanner.getState();
        if (state === 2 || state === 3) {
          await scanner.stop();
          stoppedCleanly = true;
        }
      } catch {
        // El escáner aún no terminó de iniciar o ya estaba detenido
      }
      try {
        await scanner.clear();
      } catch {
        // ignore
      }
      html5QrCodeRef.current = null;
    }

    releaseMediaTracks();
    resetScannerContainer();
    await wait(CAMERA_RELEASE_DELAY_MS);
  }, [releaseMediaTracks, resetScannerContainer]);

  const getActiveCameraDeviceId = useCallback(() => {
    const video = document.querySelector(`#${scannerIdRef.current} video`);
    return video?.srcObject?.getVideoTracks?.()?.[0]?.getSettings?.()?.deviceId ?? null;
  }, []);

  const syncCameraListAfterStart = useCallback(async (mobile, session) => {
    if (!isSessionActive(session)) return;

    try {
      // No abrir otro getUserMedia: el escáner ya tiene el stream activo.
      let cameras = await listCamerasFromDevices();
      if (!isSessionActive(session)) return;

      if (mobile && cameras.length > 1) {
        cameras = sortCamerasForMobile(cameras);
      }

      availableCamerasRef.current = cameras;
      setAvailableCameras(cameras);

      const activeId = getActiveCameraDeviceId();
      if (activeId && cameras.length > 0) {
        const activeIndex = cameras.findIndex((camera) => camera.id === activeId);
        if (activeIndex >= 0) {
          currentCameraIndexRef.current = activeIndex;
          setCurrentCameraIndex(activeIndex);
        }
      }
    } catch (error) {
      console.log("No se pudo sincronizar lista de cámaras:", error.message);
    }
  }, [getActiveCameraDeviceId, isSessionActive]);

  const tryStartCamera = useCallback(async (cameraOptions, label, session) => {
    if (!isSessionActive(session)) return false;
    if (!document.getElementById(scannerIdRef.current)) return false;

    try {
      console.log(`🔄 Intentando iniciar cámara: ${label}`);

      if (html5QrCodeRef.current) {
        await releaseCameraHardware();
        if (!isSessionActive(session)) return false;
      }

      resetScannerContainer();
      html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);

      await html5QrCodeRef.current.start(
        buildCameraConfig(cameraOptions),
        buildScannerConfig(),
        onScanSuccess,
        onScanError
      );

      if (!isSessionActive(session)) {
        await releaseCameraHardware();
        return false;
      }

      console.log(`✓ Escáner iniciado: ${label}`);
      return true;
    } catch (error) {
      console.log(`✗ No se pudo iniciar (${label}):`, error.message);
      await releaseCameraHardware();
      return false;
    }
  }, [isSessionActive, onScanSuccess, onScanError, releaseCameraHardware, resetScannerContainer]);

  // Iniciar escaneo con html5-qrcode
  const handleStartScan = async () => {
    if (startingScannerRef.current || scanning) return;

    const session = ++scannerSessionRef.current;
    startingScannerRef.current = true;
    setStartingScanner(true);

    try {
      const mobile = isMobileOrTablet();
      setIsMobileDevice(mobile);

      await releaseCameraHardware();
      if (!isSessionActive(session)) return;

      if (!document.getElementById(scannerIdRef.current)) return;

      let scannerStarted = false;
      let selectedIndex = 0;

      const config = buildScannerConfig();

      // Estrategia 1: Cámara trasera (environment) — siempre primero
      if (!scannerStarted) {
        try {
          console.log('🔄 Intentando iniciar con cámara trasera...');
          resetScannerContainer();
          html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);
          await html5QrCodeRef.current.start(
            { facingMode: 'environment' },
            config,
            onScanSuccess,
            onScanError
          );
          if (!isSessionActive(session)) { await releaseCameraHardware(); return; }
          console.log('✓ Escáner iniciado con cámara trasera/environment');
          scannerStarted = true;
        } catch (err) {
          console.log('✗ Cámara environment no disponible:', err.message);
          await releaseCameraHardware();
        }
      }

      // Estrategia 2: Cámara frontal (user)
      if (!scannerStarted && isSessionActive(session)) {
        try {
          console.log('🔄 Intentando iniciar con cámara frontal...');
          resetScannerContainer();
          html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);
          await html5QrCodeRef.current.start(
            { facingMode: 'user' },
            config,
            onScanSuccess,
            onScanError
          );
          if (!isSessionActive(session)) { await releaseCameraHardware(); return; }
          console.log('✓ Escáner iniciado con cámara frontal/user');
          scannerStarted = true;
        } catch (err) {
          console.log('✗ Cámara user no disponible:', err.message);
          await releaseCameraHardware();
        }
      }

      // Estrategia 3: Por deviceId (enumerar todas las cámaras disponibles)
      if (!scannerStarted && isSessionActive(session)) {
        try {
          console.log('🔄 Buscando cámaras por ID...');
          const devices = await Html5Qrcode.getCameras();
          console.log('📷 Cámaras encontradas:', devices.length, devices);
          if (devices && devices.length > 0) {
            // En móvil preferir trasera, en desktop tomar la primera
            const orderedDevices = mobile
              ? [...devices].sort((a, b) => {
                  const aBack = isBackCameraLabel(a.label);
                  const bBack = isBackCameraLabel(b.label);
                  if (aBack && !bBack) return -1;
                  if (!aBack && bBack) return 1;
                  return 0;
                })
              : devices;

            availableCamerasRef.current = orderedDevices.map(d => ({ id: d.id, label: d.label }));
            setAvailableCameras(orderedDevices.map(d => ({ id: d.id, label: d.label })));

            for (let i = 0; i < orderedDevices.length; i++) {
              if (!isSessionActive(session)) return;
              try {
                resetScannerContainer();
                html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);
                await html5QrCodeRef.current.start(
                  orderedDevices[i].id,
                  config,
                  onScanSuccess,
                  onScanError
                );
                if (!isSessionActive(session)) { await releaseCameraHardware(); return; }
                console.log('✓ Escáner iniciado con cámara:', orderedDevices[i].label || orderedDevices[i].id);
                selectedIndex = i;
                scannerStarted = true;
                break;
              } catch (err) {
                console.log(`✗ Error con cámara ${orderedDevices[i].label}:`, err.message);
                await releaseCameraHardware();
              }
            }
          } else {
            throw new Error('No se encontraron cámaras disponibles');
          }
        } catch (err) {
          console.log('✗ Error al buscar cámaras por ID:', err.message);
          throw err;
        }
      }

      if (!isSessionActive(session)) return;

      if (!scannerStarted) {
        throw new Error('No se pudo iniciar el escáner con ninguna estrategia disponible');
      }

      setScanning(true);
      currentCameraIndexRef.current = selectedIndex;
      setCurrentCameraIndex(selectedIndex);

      // Sincronizar lista de cámaras para el botón de cambio (no bloquea el inicio)
      void syncCameraListAfterStart(mobile, session);
    } catch (error) {
      console.error('Error al iniciar el escáner:', error);

      let errorMessage = 'No se pudo acceder a la cámara.';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = 'Permiso de cámara denegado. Por favor permite el acceso a la cámara en la configuración de tu navegador.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage = 'No se encontró ninguna cámara en tu dispositivo.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError' || error.name === 'AbortError') {
        errorMessage = 'La cámara está siendo usada por otra aplicación. Cierra otras aplicaciones e intenta de nuevo.';
      } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
        errorMessage = 'No se pudo satisfacer los requisitos de la cámara. Intenta con otra cámara.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      alert(errorMessage);
      setScanning(false);
      setAvailableCameras([]);
      availableCamerasRef.current = [];
      setCurrentCameraIndex(0);
      currentCameraIndexRef.current = 0;
      await releaseCameraHardware();
    } finally {
      startingScannerRef.current = false;
      setStartingScanner(false);
    }
  };

  const handleSwitchCamera = async () => {
    const cameras = availableCamerasRef.current;
    if (!scanning || cameras.length < 2 || switchingCamera || startingScannerRef.current) return;

    const session = scannerSessionRef.current;
    const nextIndex = (currentCameraIndexRef.current + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];

    try {
      setSwitchingCamera(true);

      const started = await tryStartCamera(
        { deviceId: nextCamera.id },
        getCameraLabel(nextCamera),
        session
      );

      if (!isSessionActive(session) || !started) {
        throw new Error("No se pudo cambiar de cámara");
      }

      currentCameraIndexRef.current = nextIndex;
      setCurrentCameraIndex(nextIndex);
    } catch (error) {
      console.error("Error al cambiar de cámara:", error);
      alert("No se pudo cambiar de cámara. Intenta detener y volver a iniciar el escaneo.");
    } finally {
      setSwitchingCamera(false);
    }
  };

  const handleStopScan = async () => {
    scannerSessionRef.current += 1;

    try {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      lastScanTextRef.current = null;
      await releaseCameraHardware();
    } catch (error) {
      console.error("Error al detener el escáner:", error);
    } finally {
      startingScannerRef.current = false;
      setStartingScanner(false);
      setScanning(false);
      setAvailableCameras([]);
      availableCamerasRef.current = [];
      setCurrentCameraIndex(0);
      currentCameraIndexRef.current = 0;
      setSwitchingCamera(false);
    }
  };

  const canSwitchCamera = scanning && isMobileDevice && availableCameras.length > 1;
  const activeCameraLabel = getCameraLabel(availableCameras[currentCameraIndex]);

  const handleConfirmarBoletos = async () => {
    if (!pendingConfirmation) return;

    setProcesandoEscaneo(true);
    const { invitadoData } = pendingConfirmation;

    try {
      const res = await qrAdminService.registrarEscaneoQR(
        evento.id,
        invitadoData.invitado_id,
        boletosConfirmar
      );

      if (res.success) {
        const mesasFinal = pendingConfirmation.mesas || '';
        const nuevoTotal = (invitadoData.boletos_escaneados || 0) + boletosConfirmar;

        const scanData = {
          id: `QR-${Date.now()}`,
          timestamp: new Date().toISOString(),
          invitadoId: invitadoData.invitado_id,
          invitado: invitadoData.invitado_nombre,
          mesa: mesasFinal || null,
          boletos: boletosConfirmar,
          boletosTotal: invitadoData.cantidad_boletos,
          boletosUsados: nuevoTotal,
          restricciones: invitadoData.restricciones || { vegetarianos: 0, alergicosMariscos: 0, celiaco: 0, alergicosLactosa: 0 },
          valido: true,
          evento: evento?.nombre_evento || 'Evento',
          mesas: mesasFinal
        };

        setLastScan(scanData);
        setScanHistory(prev => [scanData, ...prev].slice(0, 10));

        // Actualizar la lista local de invitados para reflejar el nuevo conteo
        setInvitados(prev => prev.map(inv => {
          if (inv.invitado_id === invitadoData.invitado_id) {
            return {
              ...inv,
              boletos_escaneados: (inv.boletos_escaneados || 0) + boletosConfirmar
            };
          }
          return inv;
        }));

        setPendingConfirmation(null);
        setTimeout(() => { lastScanTextRef.current = null; }, 500);
      } else {
        throw new Error(res.error);
      }
    } catch (error) {
      alert('Error al registrar: ' + error.message);
    } finally {
      setProcesandoEscaneo(false);
    }
  };

  const handleCancelarConfirmacion = () => {
    setPendingConfirmation(null);
    lastScanTextRef.current = null;
  };


  const releaseCameraHardwareRef = useRef(releaseCameraHardware);
  releaseCameraHardwareRef.current = releaseCameraHardware;

  // Ciclo de vida: montaje real / desmontaje (sin re-ejecutar al cambiar callbacks)
  useEffect(() => {
    isMountedRef.current = true;

    const handlePageHide = () => {
      scannerSessionRef.current += 1;
      void releaseCameraHardwareRef.current();
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      isMountedRef.current = false;
      scannerSessionRef.current += 1;
      window.removeEventListener("pagehide", handlePageHide);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      void releaseCameraHardwareRef.current();
    };
  }, []);

  const handleClose = () => {
    handleStopScan();
    onClose();
  };

  if (loading) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-[#1e1e1e] p-8 rounded-2xl flex flex-col items-center shadow-xl">
          <InlineSpinner size="md" />
          <p className="mt-4 text-gray-700 dark:text-gray-300">Cargando invitados del evento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-100 dark:bg-gray-900 flex flex-col">
      {/* Container principal - ocupa toda la pantalla */}
      <div className={clsx(
        "w-full h-full bg-white dark:bg-[#1a1a1a]",
        "flex flex-col"
      )}>
          {/* Header */}
          <div className="flex-shrink-0 bg-gradient-to-r from-teal-600 to-teal-500 dark:from-teal-500 dark:to-teal-400 px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <QrCode className="w-7 h-7 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    Lector de Códigos QR
                  </h1>
                  <p className="text-sm text-white/80 mt-1">
                    {evento?.nombre_evento || 'Escanea las invitaciones'}
                  </p>
                </div>
              </div>
              {onClose && (
                <button
                  className="text-white/80 hover:text-white transition-colors"
                  onClick={handleClose}
                  aria-label="Cerrar"
                >
                  <CircleX size={28} />
                </button>
              )}
            </div>
          </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 h-full">
                {/* Columna izquierda: Cámara y controles */}
                <div className="flex flex-col space-y-4 h-full">
                  <div className="bg-gray-50 dark:bg-[#23272e] rounded-xl p-4 border-2 border-gray-200 dark:border-gray-700 flex-1 flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                      <QrCode size={20} className="text-teal-600 dark:text-teal-400" />
                      Escáner
                    </h3>
                    
                    {/* Área de video/preview - cuadrado para lectura rápida */}
                    <div className="relative overflow-hidden bg-black rounded-lg mb-4 mx-auto w-full" style={{ aspectRatio: '1 / 1', maxWidth: 'min(100%, 45vh)' }}>
                      {/* Contenedor para html5-qrcode - siempre presente */}
                      <div 
                        id="qr-reader"
                        className="w-full h-full"
                      />
                      
                      {/* Placeholder cuando no está escaneando - overlay absoluto */}
                      {!scanning && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black">
                          <div className="text-center text-gray-400">
                            <QrCode size={64} className="mx-auto mb-3 opacity-50" />
                            <p className="text-sm">Presiona "Iniciar Escaneo" para comenzar</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Controles */}
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-3">
                        {!scanning ? (
                          <button
                            onClick={handleStartScan}
                            disabled={startingScanner}
                            className="flex-1 px-4 py-3 bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-colors shadow-md"
                          >
                            {startingScanner ? "Iniciando cámara..." : "Iniciar Escaneo"}
                          </button>
                        ) : (
                          <button
                            onClick={handleStopScan}
                            className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors shadow-md"
                          >
                            Detener Escaneo
                          </button>
                        )}
                      </div>
                      {canSwitchCamera && (
                        <button
                          type="button"
                          onClick={handleSwitchCamera}
                          disabled={switchingCamera}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors shadow-md"
                        >
                          <SwitchCamera size={18} />
                          {switchingCamera
                            ? "Cambiando cámara..."
                            : `Cambiar cámara (${activeCameraLabel})`}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Última lectura */}
                  {lastScan && (
                    <div className={clsx(
                      "rounded-xl p-4 border-2 overflow-y-auto max-h-96",
                      lastScan.valido 
                        ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600"
                        : "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600"
                    )}>
                      <div className="flex items-start gap-3">
                        {lastScan.valido ? (
                          <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h4 className={clsx(
                            "font-bold text-lg mb-2",
                            lastScan.valido ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"
                          )}>
                            {lastScan.valido ? '✓ Invitación Válida' : '✗ Invitación No Válida'}
                          </h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <User size={16} />
                              <span className="font-semibold">{lastScan.invitado}</span>
                            </div>
                            {lastScan.mesa && (
                              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect width="18" height="18" x="3" y="3" rx="2"/>
                                  <path d="M3 9h18"/>
                                  <path d="M9 21V9"/>
                                </svg>
                                <span>Mesa {lastScan.mesa}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <Ticket size={16} />
                              <span>{lastScan.boletos} boleto{lastScan.boletos !== 1 ? 's' : ''}</span>
                              {lastScan.boletosTotal && (
                                <span className="text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 px-2 py-0.5 rounded-full">
                                  {lastScan.boletosUsados}/{lastScan.boletosTotal} confirmados
                                </span>
                              )}
                              {lastScan.error && (
                                <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full">
                                  {lastScan.error}
                                </span>
                              )}
                            </div>
                            
                            {/* Restricciones Alimentarias */}
                            {lastScan.restricciones && (lastScan.restricciones.vegetarianos > 0 || 
                              lastScan.restricciones.alergicosMariscos > 0 || 
                              lastScan.restricciones.celiaco > 0 || 
                              lastScan.restricciones.alergicosLactosa > 0) && (
                              <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2a10 10 0 0 0-9.95 9h11.64L9.74 7.22a1 1 0 0 1 1.41-1.41l3.68 3.68a2 2 0 0 1 0 2.83l-3.68 3.68a1 1 0 0 1-1.41-1.41l3.95-3.95H2.05A10 10 0 1 0 12 2Z"/>
                                  </svg>
                                  Restricciones Alimentarias:
                                </div>
                                <div className="space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                                  {lastScan.restricciones.vegetarianos > 0 && (
                                    <div className="flex items-center gap-1.5">
                                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                      <span>Vegetarianos: <strong>{lastScan.restricciones.vegetarianos}</strong></span>
                                    </div>
                                  )}
                                  {lastScan.restricciones.alergicosMariscos > 0 && (
                                    <div className="flex items-center gap-1.5">
                                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                      <span>Alérgicos a mariscos: <strong>{lastScan.restricciones.alergicosMariscos}</strong></span>
                                    </div>
                                  )}
                                  {lastScan.restricciones.celiaco > 0 && (
                                    <div className="flex items-center gap-1.5">
                                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                                      <span>Celiacos: <strong>{lastScan.restricciones.celiaco}</strong></span>
                                    </div>
                                  )}
                                  {lastScan.restricciones.alergicosLactosa > 0 && (
                                    <div className="flex items-center gap-1.5">
                                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                      <span>Alérgicos a lactosa: <strong>{lastScan.restricciones.alergicosLactosa}</strong></span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-xs">
                              <Clock size={14} />
                              <span>{new Date(lastScan.timestamp).toLocaleTimeString('es-MX')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Columna derecha: Progreso + Historial */}
                <div className="flex flex-col space-y-4 overflow-hidden h-full">

                  {/* Progreso del Evento */}
                  {invitados.length > 0 && (() => {
                    const totalBoletos = invitados.reduce((sum, inv) => sum + (inv.cantidad_boletos || 0), 0);
                    const boletosEscaneados = invitados.reduce((sum, inv) => sum + (inv.boletos_escaneados || 0), 0);
                    const graduadosCompletos = invitados.filter(inv => inv.boletos_escaneados > 0 && inv.boletos_escaneados === inv.cantidad_boletos).length;
                    const totalGraduados = invitados.length;
                    return (
                      <div className="bg-white dark:bg-[#1e1e1e] rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex-shrink-0">
                        <h3 className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3">Progreso del Evento</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gray-50 dark:bg-[#2a2a2a] rounded-lg p-3 border border-gray-100 dark:border-gray-800 text-center">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Boletos</div>
                            <div className="text-lg font-bold">
                              <span className="text-teal-600 dark:text-teal-400">{boletosEscaneados}</span>
                              <span className="text-gray-400 dark:text-gray-500 text-sm font-normal"> / {totalBoletos}</span>
                            </div>
                          </div>
                          <div className="bg-gray-50 dark:bg-[#2a2a2a] rounded-lg p-3 border border-gray-100 dark:border-gray-800 text-center">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Graduados</div>
                            <div className="text-lg font-bold">
                              <span className="text-teal-600 dark:text-teal-400">{graduadosCompletos}</span>
                              <span className="text-gray-400 dark:text-gray-500 text-sm font-normal"> / {totalGraduados}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="bg-gray-50 dark:bg-[#23272e] rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex-1 flex flex-col overflow-hidden">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2 flex-shrink-0">
                      <Clock size={20} className="text-gray-600 dark:text-gray-400" />
                      Historial de Escaneos
                    </h3>
                    
                    {scanHistory.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <AlertCircle size={48} className="mx-auto mb-3 opacity-50" />
                        <p className="text-sm">Aún no hay escaneos registrados</p>
                      </div>
                    ) : (
                      <div className="space-y-2 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent flex-1">
                        {scanHistory.map((scan, index) => (
                          <div
                            key={scan.id}
                            className={clsx(
                              "p-3 rounded-lg border transition-all",
                              scan.valido
                                ? "bg-white dark:bg-[#1e1e1e] border-green-200 dark:border-green-800"
                                : "bg-white dark:bg-[#1e1e1e] border-red-200 dark:border-red-800",
                              index === 0 && "ring-2 ring-teal-500 dark:ring-teal-400"
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  {scan.valido ? (
                                    <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                                  ) : (
                                    <XCircle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0" />
                                  )}
                                  <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">
                                    {scan.invitado}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                                  {scan.mesa && (
                                    <span className="flex items-center gap-1">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect width="18" height="18" x="3" y="3" rx="2"/>
                                        <path d="M3 9h18"/>
                                        <path d="M9 21V9"/>
                                      </svg>
                                      Mesa {scan.mesa}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Ticket size={12} />
                                    {scan.boletos}
                                    {scan.boletosTotal && (
                                      <span className="text-xs text-gray-500 dark:text-gray-500">
                                        de {scan.boletosTotal} ({scan.boletosUsados || scan.boletos}/{scan.boletosTotal})
                                      </span>
                                    )}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {new Date(scan.timestamp).toLocaleTimeString('es-MX', { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                  </span>
                                </div>
                                
                                {/* Restricciones compactas */}
                                {scan.restricciones && (scan.restricciones.vegetarianos > 0 || 
                                  scan.restricciones.alergicosMariscos > 0 || 
                                  scan.restricciones.celiaco > 0 || 
                                  scan.restricciones.alergicosLactosa > 0) && (
                                  <div className="flex items-center gap-2 mt-1 text-xs">
                                    {scan.restricciones.vegetarianos > 0 && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                                        🥬 {scan.restricciones.vegetarianos}
                                      </span>
                                    )}
                                    {scan.restricciones.alergicosMariscos > 0 && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded">
                                        🦐 {scan.restricciones.alergicosMariscos}
                                      </span>
                                    )}
                                    {scan.restricciones.celiaco > 0 && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                                        🌾 {scan.restricciones.celiaco}
                                      </span>
                                    )}
                                    {scan.restricciones.alergicosLactosa > 0 && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                                        🥛 {scan.restricciones.alergicosLactosa}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Estadísticas */}
                  <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                    <div className="bg-white dark:bg-[#1e1e1e] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Escaneados</div>
                      <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                        {scanHistory.length}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-[#1e1e1e] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Boletos Totales</div>
                      <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                        {scanHistory.reduce((sum, scan) => sum + scan.boletos, 0)}
                      </div>
                    </div>
                  </div>

                  {/* Restricciones Alimentarias Totales */}
                  {scanHistory.length > 0 && (() => {
                    const totales = scanHistory.reduce((acc, scan) => {
                      if (scan.restricciones) {
                        acc.vegetarianos += scan.restricciones.vegetarianos || 0;
                        acc.alergicosMariscos += scan.restricciones.alergicosMariscos || 0;
                        acc.celiaco += scan.restricciones.celiaco || 0;
                        acc.alergicosLactosa += scan.restricciones.alergicosLactosa || 0;
                      }
                      return acc;
                    }, { vegetarianos: 0, alergicosMariscos: 0, celiaco: 0, alergicosLactosa: 0 });

                    const tieneRestricciones = totales.vegetarianos > 0 || 
                      totales.alergicosMariscos > 0 || 
                      totales.celiaco > 0 || 
                      totales.alergicosLactosa > 0;

                    return tieneRestricciones ? (
                      <div className="bg-white dark:bg-[#1e1e1e] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2a10 10 0 0 0-9.95 9h11.64L9.74 7.22a1 1 0 0 1 1.41-1.41l3.68 3.68a2 2 0 0 1 0 2.83l-3.68 3.68a1 1 0 0 1-1.41-1.41l3.95-3.95H2.05A10 10 0 1 0 12 2Z"/>
                          </svg>
                          Restricciones Alimentarias Totales
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {totales.vegetarianos > 0 && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-400">Vegetarianos:</span>
                              <span className="font-bold text-gray-800 dark:text-gray-100">{totales.vegetarianos}</span>
                            </div>
                          )}
                          {totales.alergicosMariscos > 0 && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-400">Mariscos:</span>
                              <span className="font-bold text-gray-800 dark:text-gray-100">{totales.alergicosMariscos}</span>
                            </div>
                          )}
                          {totales.celiaco > 0 && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-400">Celiacos:</span>
                              <span className="font-bold text-gray-800 dark:text-gray-100">{totales.celiaco}</span>
                            </div>
                          )}
                          {totales.alergicosLactosa > 0 && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              <span className="text-gray-600 dark:text-gray-400">Lactosa:</span>
                              <span className="font-bold text-gray-800 dark:text-gray-100">{totales.alergicosLactosa}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 bg-gray-50 dark:bg-[#23272e] px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  💡 <span className="font-medium">Tip:</span> Coloca el código QR frente a la cámara para escanearlo
                </p>
                {onClose && (
                  <button
                    className="px-6 py-2 bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors shadow-md"
                    onClick={handleClose}
                  >
                    Cerrar
                  </button>
                )}
              </div>
            </div>
          </div>

      {/* Modal de Confirmación de Boletos */}
      {pendingConfirmation && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm" onClick={procesandoEscaneo ? null : handleCancelarConfirmacion} />
          
          <div className="relative z-[71] mx-auto w-full max-w-sm rounded-xl bg-white dark:bg-[#1e1e1e] shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-zoom-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700 px-4 py-3">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Registrar Escaneo
              </h2>
            </div>

            {/* Content */}
            <div className="px-4 py-4">
              {/* Información del Invitado */}
              <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-3 mb-4 border border-teal-100 dark:border-teal-900/50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-teal-100 dark:bg-teal-800/50 rounded-full flex items-center justify-center border border-teal-200 dark:border-teal-700">
                    <User className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 dark:text-gray-100 text-sm leading-tight">
                      {pendingConfirmation.invitadoData.invitado_nombre}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{pendingConfirmation.invitadoData.correo || 'Sin correo'}</span>
                    </div>
                  </div>
                </div>
                {/* Stats: 2 cols para boletos + fila completa para mesa */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="bg-white dark:bg-[#1a1a1a] px-2 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800 text-center">
                    <div className="text-gray-500 text-[10px] uppercase font-medium mb-0.5">Totales</div>
                    <div className="font-bold text-gray-900 dark:text-white text-sm">{pendingConfirmation.invitadoData.cantidad_boletos}</div>
                  </div>
                  <div className="bg-white dark:bg-[#1a1a1a] px-2 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800 text-center">
                    <div className="text-gray-500 text-[10px] uppercase font-medium mb-0.5">Restantes</div>
                    <div className="font-bold text-teal-600 dark:text-teal-400 text-sm">{pendingConfirmation.boletosRestantes}</div>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#1a1a1a] px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
                  <div className="text-gray-500 text-[10px] uppercase font-medium flex-shrink-0">Mesa(s)</div>
                  <div className="font-bold text-teal-600 dark:text-teal-400 text-xs text-right break-all">
                    {pendingConfirmation.mesas || 'Sin asignar'}
                  </div>
                </div>
              </div>

              {/* Selector de Boletos */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 text-center">
                    ¿Cuántos boletos desea descontar?
                  </label>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => setBoletosConfirmar(Math.max(1, boletosConfirmar - 1))}
                      disabled={boletosConfirmar <= 1 || procesandoEscaneo}
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800 rounded-full font-bold text-xl text-gray-700 dark:text-gray-300 transition-colors shadow-sm"
                    >
                      −
                    </button>
                    
                    <div className="text-center w-20">
                      <input 
                        type="number"
                        min="1"
                        max={pendingConfirmation.boletosRestantes}
                        value={boletosConfirmar === '' ? '' : boletosConfirmar}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            setBoletosConfirmar('');
                            return;
                          }
                          const val = parseInt(e.target.value);
                          if (!isNaN(val)) {
                            setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, Math.max(1, val)));
                          }
                        }}
                        onBlur={() => {
                          if (boletosConfirmar === '' || boletosConfirmar < 1) {
                            setBoletosConfirmar(1);
                          }
                        }}
                        className="w-full text-4xl font-black text-gray-900 dark:text-white bg-transparent text-center focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg p-1 hide-arrows"
                      />
                    </div>

                    <button
                      onClick={() => setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, (boletosConfirmar || 0) + 1))}
                      disabled={boletosConfirmar >= pendingConfirmation.boletosRestantes || procesandoEscaneo}
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800 rounded-full font-bold text-xl text-gray-700 dark:text-gray-300 transition-colors shadow-sm"
                    >
                      +
                    </button>
                  </div>
                  
                  {/* Botones rápidos */}
                  {pendingConfirmation.boletosRestantes > 1 && (
                    <div className="flex justify-center gap-2 mt-3 flex-wrap">
                      <button 
                        onClick={() => setBoletosConfirmar(pendingConfirmation.boletosRestantes)}
                        className="px-3 py-1 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-xs font-bold hover:bg-teal-200 dark:hover:bg-teal-800/60 transition-colors border border-teal-200 dark:border-teal-800"
                      >
                        Todos ({pendingConfirmation.boletosRestantes})
                      </button>
                      {pendingConfirmation.boletosRestantes >= 5 && (
                        <button 
                          onClick={() => setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, (boletosConfirmar || 0) + 5))}
                          className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full text-xs font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                        >
                          +5
                        </button>
                      )}
                      {pendingConfirmation.boletosRestantes >= 10 && (
                        <button 
                          onClick={() => setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, (boletosConfirmar || 0) + 10))}
                          className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full text-xs font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                        >
                          +10
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <button
                    onClick={handleCancelarConfirmacion}
                    disabled={procesandoEscaneo}
                    className="flex-1 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmarBoletos}
                    disabled={procesandoEscaneo || boletosConfirmar === '' || boletosConfirmar < 1}
                    className="flex-1 px-3 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold text-sm transition-colors shadow-md shadow-teal-500/30 disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {procesandoEscaneo ? <InlineSpinner size="sm" className="text-white" /> : null}
                    Confirmar {boletosConfirmar !== '' ? boletosConfirmar : 0}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Mail(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}
